import Button from "@/components/Button"
import DevErrorToast from "@/components/DevErrorToast"
import Dialog from "@/components/Dialog"
import InputErrorMessage from "@/components/InputErrorMessage"
import { DateInput, SelectInput, TextInput } from "@/components/InputFields"
import InputLabel from "@/components/InputLabel"
import Spinner from "@/components/Spinner"
import Toast from "@/components/Toast"
import {
  useAddPregnancyMutation,
  useAddPrescriptionMutation,
  useUpdatePrescriptionMutation,
} from "@/data/queries"
import BloodPressureThresholds from "@/model/BloodPressureThresholds"
import { HypertensionProtocol } from "@/model/ClinicalProtocol"
import { Caregiver, Department, Organization } from "@/model/Organization"
import { Pregnancy } from "@/model/Pregnancy"
import {
  BloodPressureCuffSize,
  CreateHypertensionPrescriptionDto,
  HypertensionPrescription,
  UpdateHypertensionPrescriptionDto,
} from "@/model/Prescription"
import { cuffSizes } from "@/pages/patient-details/model"
import PregnancySelector, {
  PregnancyFieldErrors,
} from "@/pages/patient-details/prescription/PregnancySelector"
import { caregiverName } from "@/pages/patient-details/utils.ts"
import { contains } from "@/utils/array-utils"
import { zodResolver } from "@hookform/resolvers/zod"
import { LocalDate } from "@js-joda/core"
import { isEqual, orderBy } from "lodash"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { updateHypertensionPrescriptionFormSchema } from "./formSchemas"
import {
  initialPregnancySelection,
  PregnancySelection,
} from "./PregnancySelection"

type HypertensionPrescriptionDialogProps = {
  isOpen: boolean
  onClose: () => void
  organization: Organization
  bloomlifeId: string
  pregnancies?: Pregnancy[]
  defaultPregnancyId?: string
  hasActivePrescription?: boolean
  caregivers: Caregiver[]
  departments: Department[]
  prescription?: HypertensionPrescription
}

export type FormData = {
  pregnancy: PregnancySelection
  clinicalProtocolIndex?: number
  diagnosis?: string
  startDate: string
  endDate?: string
  caregiverId: string
  departmentId?: string
  cuffSize: BloodPressureCuffSize
}

const HypertensionPrescriptionDialog = ({
  isOpen,
  onClose,
  organization,
  bloomlifeId,
  pregnancies = [],
  defaultPregnancyId,
  hasActivePrescription = false,
  caregivers,
  departments,
  prescription,
}: HypertensionPrescriptionDialogProps) => {
  const caregiverOptions = [
    { title: "-- Select a provider --", value: "", disabled: true },
    ...orderBy(
      caregivers.map((caregiver) => ({
        value: caregiver.id,
        title: caregiverName(caregiver),
      })),
      ({ title }) => title.toLowerCase()
    ),
  ]

  const clinicalProtocols = organization.clinicalProtocols.filter(
    (protocol) => protocol.type === "hypertension"
  ) as HypertensionProtocol[]

  const personalizedProtocol: HypertensionProtocol | undefined =
    prescription && {
      type: "hypertension",
      name: prescription.clinicalProtocolName,
      bloodPressureThresholds: prescription.thresholds,
    }

  const hasCustomProtocol =
    personalizedProtocol && !contains(clinicalProtocols, personalizedProtocol)

  const protocols = hasCustomProtocol
    ? [personalizedProtocol, ...clinicalProtocols]
    : clinicalProtocols

  const clinicalProtocolOptions = [
    { title: "-- Select a protocol --", value: -1, disabled: true },
    ...orderBy(
      protocols.map((protocol, index) => ({
        value: index,
        title:
          hasCustomProtocol && isEqual(protocol, personalizedProtocol)
            ? `${protocol.name}*`
            : protocol.name,
      })),
      ({ title }) => title.toLowerCase()
    ),
  ]

  const departmentOptions = [
    { title: "", value: "", disabled: true },
    ...orderBy(
      departments.map((department) => ({
        title: department.name,
        value: department.id,
      })),
      ({ title }) => title.toLowerCase()
    ),
  ]

  const hasEddLessPregnancy = pregnancies.some(
    (pregnancy) => !pregnancy.estimatedDateOfDelivery && !pregnancy.deliveryDate
  )
  const canAddNewPregnancy = !hasActivePrescription

  const initialFormValues: Partial<FormData> = Object.freeze({
    pregnancy: initialPregnancySelection({
      pregnancyId: prescription?.pregnancyId ?? defaultPregnancyId,
      hasExistingPregnancies: pregnancies.length > 0,
    }),
    clinicalProtocolIndex: protocols.findIndex((protocol) =>
      isEqual(protocol, personalizedProtocol)
    ),
    diagnosis: prescription?.diagnosis,
    startDate:
      prescription?.startDate?.toString() ?? LocalDate.now().toString(),
    endDate: prescription?.endDate?.toString(),
    caregiverId: prescription?.caregiverId,
    departmentId: prescription?.caregiverDepartmentId,
    cuffSize: prescription?.cuffSize,
  })

  const { mutateAsync: updatePrescription, isPending: isUpdatingPrescription } =
    useUpdatePrescriptionMutation({
      bloomlifeId,
    })

  const { mutateAsync: addPrescription, isPending: isAddingPrescription } =
    useAddPrescriptionMutation(bloomlifeId)

  const { mutateAsync: addPregnancy, isPending: isAddingPregnancy } =
    useAddPregnancyMutation({ bloomlifeId })

  const { register, handleSubmit, formState, watch, reset, setValue } =
    useForm<FormData>({
      mode: "onChange",
      defaultValues: initialFormValues,
      resolver: zodResolver(
        updateHypertensionPrescriptionFormSchema({
          organization,
          clinicalProtocolRequired: !prescription,
          endDateRequired: prescription?.status === "inactive",
          newPregnancyEddRequired: hasEddLessPregnancy,
        })
      ),
    })

  const { errors } = formState
  const formValues = watch()
  const formHasChanges =
    !prescription || !isEqual(formValues, initialFormValues)
  const isPending =
    isUpdatingPrescription || isAddingPrescription || isAddingPregnancy

  useEffect(() => {
    if (isOpen) {
      reset(initialFormValues)
    }
    // resetting on every render would clobber the user's typing mid-edit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const handleClose = () => {
    onClose()
  }

  const onSubmit = async (formData: FormData) => {
    try {
      if (prescription) {
        await updatePrescription({
          prescriptionId: prescription.id,
          update: toUpdateHypertensionPrescriptionDto(formData),
        })
      } else {
        let resolvedPregnancyId: string
        if (formData.pregnancy.kind === "new") {
          const newPregnancy = await addPregnancy({
            bloomlifeId,
            create: {
              estimatedDateOfDelivery:
                formData.pregnancy.estimatedDateOfDelivery || null,
              deliveryDate: formData.pregnancy.deliveryDate || null,
              patientOptedInClinically:
                formData.pregnancy.patientOptedInClinically,
            },
          })
          resolvedPregnancyId = newPregnancy.id
        } else {
          resolvedPregnancyId = formData.pregnancy.pregnancyId
        }
        const clinicalProtocol =
          clinicalProtocols[formData.clinicalProtocolIndex!]
        await addPrescription({
          bloomlifeId,
          prescription: toCreateHypertensionPrescriptionDto({
            ...formData,
            pregnancyId: resolvedPregnancyId,
            clinicalProtocolName: clinicalProtocol.name,
            thresholds: clinicalProtocol.bloodPressureThresholds,
          }),
        })
      }
      handleClose()
      toast.custom((toastId) => (
        <Toast
          toastId={toastId}
          description={
            prescription
              ? "Prescription updated successfully."
              : "Prescription added successfully."
          }
        />
      ))
    } catch (error) {
      toast.custom((toastId) => (
        <DevErrorToast
          toastId={toastId}
          userMessage="Something went wrong while updating this prescription. Try again, or contact the dev team."
          error={error}
        />
      ))
    }
  }

  return (
    <Dialog
      title={
        prescription
          ? "Edit hypertension prescription"
          : "Add hypertension prescription"
      }
      isOpen={isOpen}
      onClose={handleClose}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="off"
      >
        {!prescription && (
          <PregnancySelector
            pregnancies={pregnancies}
            pregnancy={formValues.pregnancy}
            canAddNewPregnancy={canAddNewPregnancy}
            requireEstimatedDateOfDelivery={hasEddLessPregnancy}
            register={register}
            setValue={setValue}
            errors={(errors.pregnancy ?? {}) as PregnancyFieldErrors}
          />
        )}

        <div className="grid grid-cols-2 gap-8">
          <div className="flex flex-col gap-1">
            <InputLabel htmlFor="clinical-protocol" className="ml-1">
              Clinical protocol
            </InputLabel>
            <SelectInput
              id="clinical-protocol"
              options={clinicalProtocolOptions}
              disabled={!!prescription}
              value={formValues.clinicalProtocolIndex}
              {...register("clinicalProtocolIndex", { valueAsNumber: true })}
            />
            <InputErrorMessage
              message={errors?.clinicalProtocolIndex?.message}
              className="ml-1"
            />
          </div>

          <div className="flex flex-col gap-1">
            <InputLabel htmlFor="start-date" className="ml-1">
              Prescription start date
            </InputLabel>
            <DateInput
              id="start-date"
              isError={!!errors?.startDate?.message}
              {...register("startDate")}
            />
            <InputErrorMessage
              message={errors?.startDate?.message}
              className="ml-1"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <InputLabel htmlFor="diagnosis" className="ml-1">
            Diagnosis
          </InputLabel>
          <TextInput
            id="diagnosis"
            placeholder="Diagnosis"
            isError={!!errors?.diagnosis?.message}
            {...register("diagnosis")}
          />
          <InputErrorMessage
            message={errors?.diagnosis?.message}
            className="ml-1"
          />
        </div>

        {prescription && (
          <div className="grid grid-cols-2 gap-8">
            <div className="flex flex-col gap-1">
              <InputLabel htmlFor="end-date" className="ml-1">
                Prescription end date
              </InputLabel>
              <DateInput
                id="end-date"
                disabled={prescription.status === "active"}
                isError={!!errors?.endDate?.message}
                min={formValues.startDate}
                {...register("endDate")}
              />
              <InputErrorMessage
                message={errors?.endDate?.message}
                className="ml-1"
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-4">
          <div className="flex flex-col gap-1">
            <InputLabel htmlFor="provider" className="ml-1">
              Provider
            </InputLabel>
            <SelectInput
              id="caregiver"
              options={caregiverOptions}
              defaultValue=""
              {...register("caregiverId")}
            />
            <InputErrorMessage
              message={errors?.caregiverId?.message}
              className="ml-1"
            />
          </div>

          <div className="flex flex-col gap-1">
            <InputLabel htmlFor="department" className="ml-1">
              Department
            </InputLabel>
            <SelectInput
              id="department"
              options={departmentOptions}
              defaultValue=""
              {...register("departmentId")}
            />
            <InputErrorMessage
              message={errors?.departmentId?.message}
              className="ml-1"
            />
          </div>

          <div className="flex flex-col gap-1">
            <InputLabel htmlFor="cuff-size" className="ml-1">
              Cuff size
            </InputLabel>
            <SelectInput
              id="cuff-size"
              options={cuffSizes}
              {...register("cuffSize")}
            />
            <InputErrorMessage
              message={errors?.cuffSize?.message}
              className="ml-1"
            />
          </div>
        </div>

        <div className="mt-2 flex justify-end gap-3">
          <Button
            type="button"
            variant="neutral"
            onClick={handleClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            className="flex items-center gap-3 px-6"
            disabled={!formHasChanges || isPending}
          >
            {isPending && <Spinner className="size-4" />}
            Save
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

const toCreateHypertensionPrescriptionDto = (
  formData: FormData & {
    pregnancyId: string
    clinicalProtocolName: string
    thresholds: BloodPressureThresholds
  }
): CreateHypertensionPrescriptionDto => ({
  pregnancyId: formData.pregnancyId,
  type: "hypertension",
  clinicalProtocolName: formData.clinicalProtocolName,
  bloodPressureThresholds: formData.thresholds,
  diagnosis: formData.diagnosis || undefined,
  startDate: formData.startDate,
  caregiverId: formData.caregiverId || undefined,
  caregiverDepartmentId: formData.departmentId || undefined,
  deviceDistributionMethod: "home-delivery-or-reuse",
  cuffSize: formData.cuffSize || undefined,
})

const toUpdateHypertensionPrescriptionDto = (
  formData: FormData
): UpdateHypertensionPrescriptionDto => ({
  diagnosis: formData.diagnosis || undefined,
  startDate: formData.startDate,
  endDate: formData.endDate || undefined,
  caregiverId: formData.caregiverId || undefined,
  caregiverDepartmentId: formData.departmentId || undefined,
  deviceDistributionMethod: "home-delivery-or-reuse",
  cuffSize: formData.cuffSize || undefined,
})

export default HypertensionPrescriptionDialog
